import * as React from 'react';
import { CreateAppPayload, getSteps } from './CreateAppPage';
import { FormikContext, useFormik } from 'formik';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { styled } from '../../../theme';
import { FormikInputWidget } from '../../../widgets/inputs/FormikInput';
import { FormikSelectWidget } from '../../../widgets/inputs/FormikSelectWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { Keys, useKeyPress } from '../../../hooks/useKeyPress';
import { useSystem } from '../../../hooks/useSystem';
import { ColumnsLayoutWidget } from '../../../widgets/layout/ColumnsLayoutWidget';
import { BreadCrumbWidget } from '../../../widgets/header/BreadCrumbWidget';
import { PRODUCT_CONFIG_MAP } from '@journeyapps-platform/types-accounts';
import { ProjectFeatureFlags } from '@journeyapps-platform/types-accounts-hub';
import { Region, REGION_LABELS } from '../../../utils/environments';

export interface CreateAppStep1WidgetProps {
  payload: Partial<CreateAppPayload>;
  next: (payload: Partial<CreateAppPayload>) => any;
  onCancel: () => any;
}

enum FieldLabel {
  APP_NAME = 'Project name',
  REGION = 'Region'
}

type CreateAppStep1Errors = {
  app_name: string;
  region: string;
};

export const CreateAppStep1Widget: React.FC<CreateAppStep1WidgetProps> = (props) => {
  const system = useSystem();
  const regions = system.getRegions(!!props.payload.trial);
  const formik = useFormik<{ app_name: string; region: Region }>({
    initialValues: {
      app_name: props.payload.name || '',
      region: props.payload.region!
    },
    onSubmit: (event) => {
      props.next({
        ...props.payload,
        name: event.app_name.trim(),
        region: event.region
      });
    },
    validateOnBlur: false,
    validate: (values) => {
      const errors: Partial<CreateAppStep1Errors> = {};
      if (!values.app_name) {
        errors['app_name'] = 'This is a required field';
      }
      return errors;
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  const getAppName = (): FormLayoutSection => {
    let columns = [
      {
        key: 'app_name',
        elements: [
          <S.StyledInput
            label={FieldLabel.APP_NAME}
            name="app_name"
            input={{
              autoFocus: true,
              placeholder: 'Enter project name'
            }}
          />
        ]
      }
    ];

    // app feature allows for region selection
    if (PRODUCT_CONFIG_MAP[props.payload.type!].new_app_features.includes(ProjectFeatureFlags.APP)) {
      columns.push({
        key: 'region',
        elements: [
          <FormikSelectWidget
            name="region"
            label={FieldLabel.REGION}
            placeholder="Select region"
            options={regions.map((r) => {
              return {
                value: r,
                label: REGION_LABELS[r]
              };
            })}
          />
        ]
      });
    }

    return {
      key: 'app_name',
      elements: [<ColumnsLayoutWidget columns={columns} />]
    };
  };

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              action: (event, loading) => {
                props.onCancel();
              }
            },
            {
              label: 'Next',
              action: (event, loading) => {
                formik.submitForm();
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget noLogo={true} primaryLabel={'Create new project'} orientation={CenterFormOrientation.ROW}>
        <BreadCrumbWidget steps={getSteps(props.payload.type!)} currentStep={0} />
        <FormLayoutWidget sections={[getAppName(), getButtons()]} />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

namespace S {
  export const StyledInput = styled(FormikInputWidget)`
    max-width: 224px;
  `;
}
